/**
 * Token vocabulary.
 *
 * FlowScript is mostly *prose*: the majority of a document is free-form label
 * text. So the tokenizer stays deliberately shallow — it only recognises the
 * characters that carry structure (`{}()@;:,"` and comment markers) and lumps
 * everything else into `word` runs.
 *
 * Crucially, the tokenizer does NOT decide what is a keyword. `Call the police;`
 * and `call greet;` produce the same token shapes; only the parser, which knows
 * it is at the start of a statement, resolves the ambiguity. Keeping that
 * decision out of the lexer is what lets labels contain words like "end",
 * "return" or "case" without exploding.
 */
import type { SourceRange } from '../ast/source';

export type TokenType =
  /** A run of characters with no structural meaning. Most label text. */
  | 'word'
  /** A double-quoted string literal. `value` is the raw text including quotes. */
  | 'string'
  | 'lbrace'
  | 'rbrace'
  | 'lparen'
  | 'rparen'
  | 'semicolon'
  | 'colon'
  | 'comma'
  /** `@`, introducing an annotation. */
  | 'at'
  /** `\` at the start of a statement, escaping keyword interpretation. */
  | 'escape'
  | 'line-comment'
  | 'block-comment'
  | 'eof';

export interface Token {
  readonly type: TokenType;
  /** Exact source text of the token. */
  readonly value: string;
  /**
   * Lower-cased `value` for `word` tokens (keyword matching is
   * case-insensitive); for a `string` token, the *unescaped* contents without
   * the surrounding quotes. Empty for punctuation.
   */
  readonly normalized: string;
  readonly range: SourceRange;
  /** True when at least one line break sits between this token and the previous one. */
  readonly precededByNewline: boolean;
  /** Index into the full token list, comments included. */
  readonly index: number;
}

/**
 * Every reserved word. Reserved-ness is positional — see the module comment —
 * so this set is consulted by the parser, not the tokenizer.
 */
export const KEYWORDS = new Set<string>([
  'start',
  'end',
  'if',
  'else',
  'while',
  'repeat',
  'until',
  'for',
  'foreach',
  'each',
  'in',
  'switch',
  'case',
  'default',
  'function',
  'call',
  'return',
  'break',
  'continue',
  'yes',
  'no',
  'true',
  'false',
]);

/** Words that may begin a statement and change how it is parsed. */
export const STATEMENT_KEYWORDS = new Set<string>([
  'start',
  'end',
  'if',
  'else',
  'while',
  'repeat',
  'until',
  'for',
  'foreach',
  'switch',
  'case',
  'default',
  'function',
  'call',
  'return',
  'break',
  'continue',
]);

/** Words accepted where a positive branch label is expected. */
export const YES_KEYWORDS = new Set<string>(['yes', 'true']);
/** Words accepted where a negative branch label is expected. */
export const NO_KEYWORDS = new Set<string>(['no', 'false']);

export function isComment(token: Token): boolean {
  return token.type === 'line-comment' || token.type === 'block-comment';
}

export function isWord(token: Token, normalized: string): boolean {
  return token.type === 'word' && token.normalized === normalized;
}

/** Human-readable token name for diagnostics. */
export function describeToken(token: Token): string {
  switch (token.type) {
    case 'eof':
      return 'end of file';
    case 'lbrace':
      return "'{'";
    case 'rbrace':
      return "'}'";
    case 'lparen':
      return "'('";
    case 'rparen':
      return "')'";
    case 'semicolon':
      return "';'";
    case 'colon':
      return "':'";
    case 'comma':
      return "','";
    case 'at':
      return "'@'";
    case 'escape':
      return "'\\'";
    case 'string':
      return `string ${token.value}`;
    case 'line-comment':
    case 'block-comment':
      return 'comment';
    case 'word':
      return `'${token.value}'`;
  }
}

export function makeEofToken(range: SourceRange, index: number): Token {
  return {
    type: 'eof',
    value: '',
    normalized: '',
    range,
    precededByNewline: false,
    index,
  };
}
